import { Box, Button, CircularProgress, Tab, Tabs } from "@mui/material";
import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../../contexts/AuthContext";
import MyModal from "../MyModal";
import {
  bodyshellService,
  IBodyShellDetail,
  IBodyShellImageSlot,
  IBodyShellRequest,
} from "../../services/bodyshellService";
import { imageUrlToBase64 } from "../../services/ConvertToBase64";
import StepIdentification, { IStep1Data } from "./steps/StepIdentification";
import StepImages, {
  BodyShellImageFileNames,
  BodyShellImageValues,
} from "./steps/StepImages/StepImages";
import StepVisualization from "./steps/StepVisualization/StepVisualization";
import {
  backButtonSx,
  closeIconSx,
  footerSx,
  nextButtonSx,
  stepContentSx,
  tabsFillerSx,
  tabsSx,
} from "./ModalBodyShell.styles";

interface IModalBodyShell {
  modalChange: (open: boolean) => void;
  onError?: (message: string) => void;
  onSuccess?: (message: string) => void;
  mode?: "add" | "edit" | "view";
  data?: IBodyShellDetail;
}

const STEPS = [
  "bodyshell.steps.identification",
  "bodyshell.steps.images",
  "bodyshell.steps.visualization",
] as const;

const INITIAL_STEP1: IStep1Data = {
  name: "",
  scale: "",
  vehicleModelId: "",
};

function buildStep1FromData(data: IBodyShellDetail): IStep1Data {
  const firstVehicle = data.vehicles?.[0];
  return {
    name: data.name,
    scale: data.scale,
    vehicleModelId: firstVehicle ? String(firstVehicle.vehicleId) : "",
    vehicleName: firstVehicle?.vehicleName ?? "",
  };
}

function buildImagesFromData(data: IBodyShellDetail): BodyShellImageValues {
  return {
    car: data.carPath,
    frontLeftC: data.frontLeftCPath,
    frontLeft: data.frontLeftPath,
    frontRightC: data.frontRightCPath,
    frontRight: data.frontRightPath,
    rearLeftC: data.rearLeftCPath,
    rearLeft: data.rearLeftPath,
    rearRightC: data.rearRightCPath,
    rearRight: data.rearRightPath,
    frontLeftCOw: data.frontLeftCOwPath,
    frontRightCOw: data.frontRightCOwPath,
    rearLeftCOw: data.rearLeftCOwPath,
    rearRightCOw: data.rearRightCOwPath,
    trunk: data.trunkPath,
    hood: data.hoodPath,
    sunRoofC: data.sunRoofCPath,
    sunRoof: data.sunRoofPath,
  };
}

const IMAGE_REQUEST_KEYS: Record<
  keyof BodyShellImageValues,
  keyof IBodyShellRequest
> = {
  car: "carImageBase64",
  frontLeftC: "frontLeftCImageBase64",
  frontLeft: "frontLeftImageBase64",
  frontRightC: "frontRightCImageBase64",
  frontRight: "frontRightImageBase64",
  rearLeftC: "rearLeftCImageBase64",
  rearLeft: "rearLeftImageBase64",
  rearRightC: "rearRightCImageBase64",
  rearRight: "rearRightImageBase64",
  frontLeftCOw: "frontLeftCOwImageBase64",
  frontRightCOw: "frontRightCOwImageBase64",
  rearLeftCOw: "rearLeftCOwImageBase64",
  rearRightCOw: "rearRightCOwImageBase64",
  trunk: "trunkImageBase64",
  hood: "hoodImageBase64",
  sunRoofC: "sunRoofCImageBase64",
  sunRoof: "sunRoofImageBase64",
};

function mapImagesToPayload(
  values: BodyShellImageValues,
): Partial<IBodyShellRequest> {
  return Object.entries(values).reduce<Partial<IBodyShellRequest>>(
    (result, [slot, value]) => {
      if (value) {
        Object.assign(result, {
          [IMAGE_REQUEST_KEYS[slot as keyof BodyShellImageValues]]: value,
        });
      }
      return result;
    },
    {},
  );
}

export default function ModalBodyShell({
  modalChange,
  onError,
  onSuccess,
  mode = "add",
  data,
}: IModalBodyShell) {
  const { t } = useTranslation();
  const { tokenHeaders } = useContext(AuthContext);
  const [activeStep, setActiveStep] = useState(() => (mode === "view" ? 2 : 0));
  const [step1Data, setStep1Data] = useState<IStep1Data>(() =>
    (mode === "edit" || mode === "view") && data
      ? buildStep1FromData(data)
      : INITIAL_STEP1,
  );
  const [payload, setPayload] = useState<Partial<IBodyShellRequest>>(() => {
    if ((mode === "edit" || mode === "view") && data) {
      return {
        name: data.name,
        scale: data.scale,
        vehicleId: data.vehicles?.[0]?.vehicleId,
      };
    }
    return {};
  });
  const [imageValues, setImageValues] = useState<BodyShellImageValues>(() =>
    (mode === "edit" || mode === "view") && data
      ? buildImagesFromData(data)
      : {},
  );

  const [imageFileNames, setImageFileNames] = useState<BodyShellImageFileNames>(
    () =>
      (mode === "edit" || mode === "view") && data
        ? buildImagesFromData(data)
        : {},
  );
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [stepValidities, setStepValidities] = useState<boolean[]>([
    (mode === "edit" || mode === "view") && !!data,
    true,
    true,
  ]);

  const isFirstStep = activeStep === 0;
  const isLastStep = activeStep === STEPS.length - 1;
  const isNextDisabled = !stepValidities[activeStep] || isChecking || isSaving;

  const handleValidChange = useCallback(
    (valid: boolean) => {
      setStepValidities((prev) => {
        if (prev[activeStep] === valid) {
          return prev;
        }
        const next = [...prev];
        next[activeStep] = valid;
        return next;
      });
    },
    [activeStep],
  );

  const mergeStep1IntoPayload = () => {
    setPayload((prev) => ({
      ...prev,
      name: step1Data.name,
      scale: step1Data.scale as number,
      vehicleId: parseInt(step1Data.vehicleModelId, 10),
    }));
  };

  const handleStep0 = async (): Promise<boolean> => {
    const nameIsUnchanged =
      (mode === "edit" || mode === "view") && data?.name === step1Data.name;
    if (!nameIsUnchanged) {
      setIsChecking(true);
      try {
        const { data: nameExists } = await bodyshellService.checkName(
          step1Data.name,
          tokenHeaders,
        );
        if (nameExists) {
          onError?.(t("bodyshell.step1.nameExists"));
          return false;
        }
      } catch {
        // se a verificação falhar, permite avançar
      } finally {
        setIsChecking(false);
      }
    }
    mergeStep1IntoPayload();
    return true;
  };

  const handleImageChange = (values: BodyShellImageValues) => {
    setImageValues(values);
    setPayload((previous) => ({
      ...previous,
      ...mapImagesToPayload(values),
    }));
  };

  const handleImageFileNameChange = (
    slot: IBodyShellImageSlot,
    fileName: string,
  ) => {
    setImageFileNames((previous) => ({
      ...previous,
      [slot]: fileName,
    }));
  };

  const buildRequestPayload = async (): Promise<IBodyShellRequest> => {
    const imageEntries = await Promise.all(
      Object.entries(imageValues).map(async ([slot, value]) => {
        if (!value) {
          return [slot, value] as const;
        }
        const base64 =
          value.startsWith("http://") || value.startsWith("https://")
            ? await imageUrlToBase64(value)
            : value;

        return [slot, base64] as const;
      }),
    );

    const imagesBase64 = Object.fromEntries(imageEntries);

    const requestPayload = {
      name: step1Data.name,
      scale: Number(step1Data.scale),
      vehicleId: Number(step1Data.vehicleModelId),
      ...mapImagesToPayload(imagesBase64),
    } as IBodyShellRequest;

    if (mode === "edit" || mode === "view") {
      requestPayload.active = data?.active;
    }
    return requestPayload;
  };

  const handleSave = async () => {
    if (isSaving) {
      return;
    }
    setIsSaving(true);
    try {
      const requestPayload = await buildRequestPayload();
      if (mode === "add") {
        await bodyshellService.create(requestPayload, tokenHeaders);
      } else {
        if (!data?.id) {
          onError?.(t("bodyshell.editedError"));
          return;
        }
        await bodyshellService.update(data.id, requestPayload, tokenHeaders);
      }
      onSuccess?.(
        t(
          mode === "add" ? "bodyshell.addedSuccess" : "bodyshell.editedSuccess",
        ),
      );
      modalChange(false);
    } catch (error) {
      onError?.(
        t(mode === "add" ? "bodyshell.addedError" : "bodyshell.editedError"),
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    if (isLastStep) {
      await handleSave();
      return;
    }

    let canAdvance = true;
    if (activeStep === 0) {
      canAdvance = await handleStep0();
    }
    if (canAdvance) {
      setActiveStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (!isFirstStep) {
      setActiveStep((prev) => prev - 1);
    }
  };

  const title =
    mode === "add"
      ? t("bodyshell.modal.titleAdd")
      : t("bodyshell.modal.titleEdit");

  return (
    <MyModal title={title} modalChange={modalChange} closeIconSx={closeIconSx}>
      <Tabs value={activeStep} sx={tabsSx}>
        {STEPS.map((stepKey, index) => (
          <Tab key={stepKey} label={`${index + 1}. ${t(stepKey)}`} />
        ))}
        <Box sx={tabsFillerSx} />
      </Tabs>

      <Box sx={stepContentSx}>
        {activeStep === 0 && (
          <StepIdentification
            formData={step1Data}
            onChange={setStep1Data}
            onValidChange={handleValidChange}
            mode={mode}
          />
        )}
        {activeStep === 1 && (
          <StepImages
            values={imageValues}
            onError={onError}
            onChange={handleImageChange}
            onFileNameChange={handleImageFileNameChange}
            onValidChange={handleValidChange}
          />
        )}
        {activeStep === 2 && (
          <StepVisualization values={imageValues} fileNames={imageFileNames} />
        )}
      </Box>

      <Box sx={footerSx}>
        {isSaving ? (
          <Box
            sx={{
              alignItems: "center",
              justifyContent: "center",
              minWidth: 100,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <>
            {!isFirstStep && (
              <Button
                sx={backButtonSx}
                variant="outlined"
                onClick={handleBack}
                disableElevation
              >
                {t("bodyshell.button.back")}
              </Button>
            )}

            <Button
              sx={nextButtonSx}
              variant="contained"
              onClick={handleNext}
              disabled={isNextDisabled}
              disableElevation
            >
              {isLastStep
                ? t("bodyshell.button.save")
                : t("bodyshell.button.next")}
            </Button>
          </>
        )}
      </Box>
    </MyModal>
  );
}
